import { useEffect, lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useApiClient } from "./providers/ApiClientProvider";
import { useQueryClient } from "@tanstack/react-query";
import { cleanupTutorialResourcesSafe } from "./lib/tutorials/cleanup";
import { useAuthStore } from "./stores/auth";
import { useShallow } from "zustand/shallow";
import { FeedbackButton } from "./components/FeedbackButton";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { useSessionValidation } from "./hooks/useSessionValidation";
import { isDevOrStaging } from "./lib/colorOverrides";

// Critical routes - eagerly loaded (auth flow and main app shell)
import { LoginPage } from "./features/auth/DevLoginPage";
import { RegisterPage } from "./features/auth/RegisterPage";
import { VerifyEmailPage } from "./features/auth/VerifyEmailPage";
import { CheckEmailPage } from "./features/auth/CheckEmailPage";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { LandingPage } from "./features/marketing/LandingPage";
import { AuthenticatedLayout } from "./layouts/AuthenticatedLayout";
import { ClassesPage } from "./features/classes/ClassesPage";
import { AssignmentDetailPage } from "./features/assignments/AssignmentDetailPage";
import { PortalDocumentReplayPage } from "./features/assignments/PortalDocumentReplayPage";
import { sanitizeRedirect } from "./lib/security/redirects";

// Lazy-loaded routes - split into separate chunks
// Medium priority routes
const ClassDetailPage = lazy(() => import("./features/classes/ClassDetailPage").then(m => ({ default: m.ClassDetailPage })));
const SettingsLayout = lazy(() => import("./features/settings/SettingsLayout").then(m => ({ default: m.SettingsLayout })));
const AccountSection = lazy(() => import("./features/settings/AccountSection").then(m => ({ default: m.AccountSection })));
const IntegrationsSection = lazy(() => import("./features/settings/IntegrationsSection").then(m => ({ default: m.IntegrationsSection })));
const VocabularySection = lazy(() => import("./features/settings/VocabularySection").then(m => ({ default: m.VocabularySection })));
const CriteriaTemplatesSection = lazy(() => import("./features/settings/CriteriaTemplatesSection").then(m => ({ default: m.CriteriaTemplatesSection })));
const EmailTemplatesSection = lazy(() => import("./features/settings/EmailTemplatesSection").then(m => ({ default: m.EmailTemplatesSection })));
const LegalSection = lazy(() => import("./features/settings/LegalSection").then(m => ({ default: m.LegalSection })));
const BillingSuccessPage = lazy(() => import("./features/billing/BillingSuccessPage").then(m => ({ default: m.BillingSuccessPage })));
const BillingCanceledPage = lazy(() => import("./features/billing/BillingCanceledPage").then(m => ({ default: m.BillingCanceledPage })));
const PricingPage = lazy(() => import("./features/billing/PricingPage").then(m => ({ default: m.PricingPage })));
const TermsOfServicePage = lazy(() => import("./features/legal/TermsOfServicePage").then(m => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazy(() => import("./features/legal/PrivacyPolicyPage").then(m => ({ default: m.PrivacyPolicyPage })));
const SecurityPage = lazy(() => import("./features/legal/SecurityPage").then(m => ({ default: m.SecurityPage })));
const DataProcessingAddendumPage = lazy(() => import("./features/legal/DataProcessingAddendumPage").then(m => ({ default: m.DataProcessingAddendumPage })));
const ClassroomOAuthPage = lazy(() => import("./features/legal/ClassroomOAuthPage").then(m => ({ default: m.ClassroomOAuthPage })));

// Low priority routes (admin, portal, errors)
const AdminUsersPage = lazy(() => import("./features/admin/AdminUsersPage").then(m => ({ default: m.AdminUsersPage })));
// Dev tools (only loaded in dev/staging)
const ToolboxIndexPage = lazy(() =>
  import("./features/toolbox/ToolboxIndexPage")
    .then(m => ({ default: m.ToolboxIndexPage }))
);
const ColorsDevPage = lazy(() => import("./features/dev/ColorsDevPage").then(m => ({ default: m.ColorsDevPage })));
const ExtensionTestPage = lazy(() =>
  import("./features/extensionTest/ExtensionTestPage")
    .then(m => ({ default: m.ExtensionTestPage }))
);
const DebugReplayPage = lazy(() =>
  import("./features/extensionTest/DebugReplayPage")
    .then(m => ({ default: m.DebugReplayPage }))
);
const AdminFeedbackPage = lazy(() => import("./features/admin/AdminFeedbackPage").then(m => ({ default: m.AdminFeedbackPage })));
const AdminFeatureFlagsPage = lazy(() => import("./features/admin/AdminFeatureFlagsPage").then(m => ({ default: m.AdminFeatureFlagsPage })));
const AdminForceUpdatePage = lazy(() => import("./features/admin/AdminForceUpdatePage").then(m => ({ default: m.AdminForceUpdatePage })));
const AdminMfaSetupPage = lazy(() => import("./features/admin/AdminMfaSetupPage").then(m => ({ default: m.AdminMfaSetupPage })));
const AdminMfaGuard = lazy(() => import("./features/admin/AdminMfaGuard").then(m => ({ default: m.AdminMfaGuard })));
const AIDetectorLandingPage = lazy(() => import("./features/marketing/AIDetectorLandingPage").then(m => ({ default: m.AIDetectorLandingPage })));
const StudentPortalPage = lazy(() => import("./features/portal/StudentPortalPage"));
const PortalPaperViewerPage = lazy(() => import("./features/portal/PortalPaperViewerPage"));
const StudentAssignmentWritePage = lazy(() => import("./features/portal/StudentAssignmentWritePage"));
const NotFoundPage = lazy(() => import("./features/errors/NotFoundPage").then(m => ({ default: m.NotFoundPage })));
const AccountDeletedPage = lazy(() => import("./features/account/AccountDeletedPage").then(m => ({ default: m.AccountDeletedPage })));

// Module-level flag to track cleanup across component remounts (StrictMode compatibility)
let globalCleanupCompleted = false;

// Loading fallback for lazy-loaded routes
function RouteLoadingFallback() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      width: "100%"
    }}>
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "4px solid #f3f4f6",
          borderTop: "4px solid #3b82f6",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto"
        }} />
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, hydrated } = useAuthStore(useShallow((state) => ({ user: state.user, hydrated: state.hydrated })));
  const location = useLocation();
  if (!hydrated) {
    return null;
  }
  if (!user) {
    const nextTarget = sanitizeRedirect(`${location.pathname}${location.search}` || "/");
    const params = new URLSearchParams();
    if (nextTarget) {
      params.set("next", nextTarget);
    }
    const redirectPath = params.toString() ? `/login?${params.toString()}` : "/login";
    return <Navigate to={redirectPath} replace />;
  }
  return children;
}

// Redirects legacy /criteria-templates/:templateId to /settings/criteria-templates/:templateId
function CriteriaTemplateRedirect() {
  const location = useLocation();
  const templateId = location.pathname.split("/").pop();
  return <Navigate to={`/settings/criteria-templates/${templateId}`} replace />;
}

// Settings index redirect - goes to first visible section
function SettingsIndexRedirect() {
  return <Navigate to="/settings/account" replace />;
}

// Home page - shows landing for unauthenticated, redirects to /classes for authenticated
function HomePage() {
  const { user, hydrated } = useAuthStore(useShallow((state) => ({ user: state.user, hydrated: state.hydrated })));

  if (!hydrated) {
    return null;
  }

  if (user) {
    return <Navigate to="/classes" replace />;
  }

  return <LandingPage />;
}

export default function App() {
  const location = useLocation();
  const isPublicPage = location.pathname.startsWith("/share/") || location.pathname.startsWith("/portal/");
  const { user } = useAuthStore(useShallow((state) => ({ user: state.user })));
  const api = useApiClient();
  const queryClient = useQueryClient();

  // Proactively validate session to catch expiration before user actions fail
  useSessionValidation();

  // Global cleanup of tutorial resources on every page load (only for authenticated users)
  useEffect(() => {
    // Use module-level flag to prevent duplicate cleanup across remounts
    // This handles React StrictMode double-invocation and hot reloading
    if (globalCleanupCompleted) {
      return;
    }

    // Only run cleanup if user is authenticated - prevents 401 errors for logged-out users
    if (!user) {
      return;
    }

    globalCleanupCompleted = true;

    // Fire-and-forget cleanup - don't block app loading
    // Always clean up tutorial resources on page load
    void cleanupTutorialResourcesSafe(api, queryClient, {
      invalidateCaches: true, // Invalidate caches to refresh UI after cleanup
      clearState: true, // Always clear tutorial state flags
    });
  }, [user, api, queryClient]); // Re-run when user logs in

  return (
    <>
      <ImpersonationBanner />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Auth routes (public) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/check-email" element={<CheckEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Billing routes (public - users return here from Stripe) */}
          <Route path="/billing/success" element={<BillingSuccessPage />} />
          <Route path="/billing/canceled" element={<BillingCanceledPage />} />
          {/* Account deleted confirmation (public - user is logged out) */}
          <Route path="/account-deleted" element={<AccountDeletedPage />} />
          {/* Pricing page - requires auth to check subscription status */}
          <Route
            path="/pricing"
            element={
              <RequireAuth>
                <PricingPage />
              </RequireAuth>
            }
          />
          {/* Legal routes (public) */}
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/dpa" element={<DataProcessingAddendumPage />} />
          <Route path="/classroom-oauth" element={<ClassroomOAuthPage />} />
          {/* AI Detector marketing page (public) */}
          <Route path="/ai-detection" element={<AIDetectorLandingPage />} />
          {/* Home page (public landing or authenticated classes) */}
          <Route path="/" element={<HomePage />} />
          {/* Authenticated routes with shared layout */}
          <Route
            element={
              <RequireAuth>
                <AuthenticatedLayout />
              </RequireAuth>
            }
          >
            {/* Classes with modal routes */}
            <Route path="/classes" element={<ClassesPage />} />
            <Route path="/classes/new" element={<ClassesPage />} />
            <Route path="/classes/import" element={<ClassesPage />} />
            <Route path="/classes/:classId/archive" element={<ClassesPage />} />
            <Route path="/classes/:classId/delete" element={<ClassesPage />} />
            <Route path="/classes/:classId" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/edit" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/students/new" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/students/:studentId/edit" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/students/:studentId/archive" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/students/:studentId/delete" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/assignments/new" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/archive" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/delete" element={<ClassDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/edit" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/add" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/edit" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/delete" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/view" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/preview" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/share" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/papers/:documentId/replay" element={<PortalDocumentReplayPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/criteria" element={<AssignmentDetailPage />} />
            <Route path="/classes/:classId/assignments/:assignmentId/students/:studentId/edit-email" element={<AssignmentDetailPage />} />
            {/* Settings with nested routes */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<SettingsIndexRedirect />} />
              <Route path="account" element={<AccountSection />} />
              <Route path="account/upgrade" element={<AccountSection />} />
              <Route path="account/delete" element={<AccountSection />} />
              <Route path="legal" element={<LegalSection />} />
              <Route path="integrations" element={<IntegrationsSection />} />
              <Route path="integrations/connect" element={<IntegrationsSection />} />
              <Route path="integrations/disconnect" element={<IntegrationsSection />} />
              <Route path="integrations/reconnect" element={<IntegrationsSection />} />
              <Route path="vocabulary" element={<VocabularySection />} />
              <Route path="criteria-templates" element={<CriteriaTemplatesSection />} />
              <Route path="criteria-templates/:templateId" element={<CriteriaTemplatesSection />} />
              <Route path="email-templates" element={<EmailTemplatesSection />} />
              <Route path="email-templates/new" element={<EmailTemplatesSection />} />
              <Route path="email-templates/:templateId" element={<EmailTemplatesSection />} />
            </Route>
            {/* Admin routes - protected by MFA guard when feature flag is enabled */}
            <Route path="/admin/users" element={<AdminMfaGuard><AdminUsersPage /></AdminMfaGuard>} />
            <Route path="/admin/feedback" element={<AdminMfaGuard><AdminFeedbackPage /></AdminMfaGuard>} />
            <Route path="/admin/feedback/:feedbackId" element={<AdminMfaGuard><AdminFeedbackPage /></AdminMfaGuard>} />
            <Route path="/admin/feature-flags" element={<AdminMfaGuard><AdminFeatureFlagsPage /></AdminMfaGuard>} />
            <Route path="/admin/force-update" element={<AdminMfaGuard><AdminForceUpdatePage /></AdminMfaGuard>} />
            {/* MFA setup page is NOT guarded - you need to access it to set up MFA */}
            <Route path="/admin/mfa" element={<AdminMfaSetupPage />} />
            {/* Dev tools - only available in development/staging */}
            {isDevOrStaging() && (
              <>
                <Route
                  path="/toolbox"
                  element={
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <ToolboxIndexPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/toolbox/colors"
                  element={
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <ColorsDevPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/toolbox/extension-test"
                  element={
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <ExtensionTestPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/toolbox/debug-replay/:documentId?"
                  element={
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <DebugReplayPage />
                    </Suspense>
                  }
                />
              </>
            )}
          </Route>
          {/* Legacy /criteria-templates route redirects */}
          <Route
            path="/criteria-templates"
            element={<Navigate to="/settings/criteria-templates" replace />}
          />
          <Route
            path="/criteria-templates/:templateId"
            element={<CriteriaTemplateRedirect />}
          />
          {/* Student Portal routes (public - no auth required) */}
          <Route path="/portal/:portalKey" element={<StudentPortalPage />} />
          <Route path="/portal/:portalKey/paper/:documentId" element={<PortalPaperViewerPage />} />
          <Route path="/portal/:portalKey/assignment/:assignmentId/write" element={<StudentAssignmentWritePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {!isPublicPage && user && <FeedbackButton />}
    </>
  );
}
